/**
 * SCM15 — BoM order status workflow.
 *
 * Defines the canonical monotonic state machine for `BomItem.status`:
 *
 *   planned → rfq-out → quoted → ordered → in-transit → on-site → commissioned
 *
 * Each state has a stable ordinal so transitions are checked numerically.
 * The spec calls the second state `rfq` but the existing Phase 1 / Phase 2
 * codebase uses `rfq-out` (see `BomItemStatus` in
 * `src/types/supplyChain.ts`). The state-machine respects the existing
 * literal so we don't churn the rest of the codebase.
 *
 * Monotonicity rule (conservation law — from CLAUDE.md):
 *   - Forward steps allowed any distance (planned → ordered is fine; the
 *     user might skip "quoted" when issuing a sole-source PO).
 *   - Backward steps require an explicit `revert: true` flag and emit a
 *     `status_revert` audit event tagged with the previous status. This
 *     mirrors the way nuclear-grade procurement systems require a
 *     justification for any walk-back of state.
 *
 * Pure functions live here. The Firestore writer lives in
 * `bomStatusChangeLog.ts` so this module stays unit-testable without any
 * Firebase mocks.
 */
import type { BomItem, BomItemStatus } from '../types/supplyChain';

/**
 * Ordered enumeration of all statuses. Index is the monotonic ordinal —
 * use `STATUS_ORDINAL[status]` to compare two statuses numerically.
 *
 * Index 0 is the initial state (`planned`); index 6 is the terminal state
 * (`commissioned`). Statuses are intentionally not reordered relative to
 * `BomItemStatus` so callers can iterate either way.
 */
export const BOM_STATUS_ORDER: readonly BomItemStatus[] = [
  'planned',
  'rfq-out',
  'quoted',
  'ordered',
  'in-transit',
  'on-site',
  'commissioned',
] as const;

/** Map status → 0-indexed ordinal. Built once at module load. */
export const STATUS_ORDINAL: Record<BomItemStatus, number> = Object.freeze(
  BOM_STATUS_ORDER.reduce<Record<BomItemStatus, number>>((acc, s, i) => {
    acc[s] = i;
    return acc;
  }, {} as Record<BomItemStatus, number>),
);

/** Human-readable labels for the BoM table status pill. */
export const STATUS_LABELS: Record<BomItemStatus, string> = {
  'planned':     'Planned',
  'rfq-out':     'RFQ out',
  'quoted':      'Quoted',
  'ordered':     'Ordered',
  'in-transit':  'In transit',
  'on-site':     'On site',
  'commissioned':'Commissioned',
};

/**
 * Short rationale for each forward step. Surfaced in the audit timeline
 * popover so the reader knows what the platform considers a valid
 * transition. Never used as a hard rule — callers always pass their
 * own reason string.
 */
export const FORWARD_TRANSITION_RATIONALE: Record<BomItemStatus, string> = {
  'planned':     'Initial state — BoM item created.',
  'rfq-out':     'RFQ issued to suppliers.',
  'quoted':      'Vendor quote received and recorded.',
  'ordered':     'Purchase order placed with awarded supplier.',
  'in-transit':  'Vendor confirmed shipment; goods in transit.',
  'on-site':     'Equipment received and confirmed on site.',
  'commissioned':'Commissioning completed; item operational.',
};

// ─────────────────────────────────────────────────────────────────────────────
// Pure transition logic
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Result of validating a proposed status transition. `kind` distinguishes
 * the three legal outcomes (`forward`, `revert`, `noop`) from the single
 * illegal one (`invalid`). Callers pattern-match on `kind` before
 * writing to Firestore.
 */
export type StatusTransitionValidation =
  | { kind: 'forward'; fromOrdinal: number; toOrdinal: number }
  | { kind: 'revert'; fromOrdinal: number; toOrdinal: number }
  | { kind: 'noop'; ordinal: number }
  | { kind: 'invalid'; reason: string };

/**
 * Validate a proposed BoM status transition.
 *
 * - Same status → `noop`. Callers should skip the write.
 * - Higher ordinal → `forward`. Always allowed.
 * - Lower ordinal → `revert`. Only allowed when `revert === true`.
 * - Unknown status → `invalid`.
 *
 * Pure / no IO; safe to call on every keystroke if needed.
 */
export function validateStatusTransition(
  current: BomItemStatus,
  next: BomItemStatus,
  opts: { revert?: boolean } = {},
): StatusTransitionValidation {
  const from = STATUS_ORDINAL[current];
  const to = STATUS_ORDINAL[next];
  if (from === undefined) return { kind: 'invalid', reason: `Unknown current status: ${String(current)}` };
  if (to === undefined) return { kind: 'invalid', reason: `Unknown target status: ${String(next)}` };
  if (from === to) return { kind: 'noop', ordinal: from };
  if (to > from) return { kind: 'forward', fromOrdinal: from, toOrdinal: to };
  // to < from — revert path
  if (!opts.revert) {
    return {
      kind: 'invalid',
      reason:
        `Backward transition ${current} → ${next} requires an explicit revert flag (monotonic invariant).`,
    };
  }
  return { kind: 'revert', fromOrdinal: from, toOrdinal: to };
}

/**
 * Apply a status transition to a `BomItem`. Returns a NEW item — never
 * mutates. When the transition is invalid the function throws so the
 * caller can surface a toast; when it's a noop the original item is
 * returned by reference.
 *
 * Use the `revert: true` opt-in for backward steps. Forward steps are
 * unconditional.
 */
export function applyStatusTransition(
  item: BomItem,
  next: BomItemStatus,
  opts: { revert?: boolean } = {},
): BomItem {
  const v = validateStatusTransition(item.status, next, opts);
  if (v.kind === 'invalid') throw new Error(v.reason);
  if (v.kind === 'noop') return item;
  return { ...item, status: next };
}

// ─────────────────────────────────────────────────────────────────────────────
// Audit-log entry shape (Firestore document body)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Single row written to `bom_order_status_changes/{eventId}` whenever a
 * BoM item status transitions.
 *
 * Field semantics:
 *   - `kind: 'forward' | 'revert'` — matches the validator outcome.
 *     `noop` is never persisted (no write to make).
 *   - `actorUid` / `actorEmail` — denormalised from auth state at write
 *     time so the Suppliers/BoM tab can render attribution without an
 *     extra user-doc lookup.
 *   - `reason` — optional free-text note from the UI dialog. Reverts
 *     require a non-empty reason (enforced at the call site, not here).
 *   - `expectedShipDate` / `requiredOnSiteDate` — sometimes carried so
 *     SCM16's alert deriver can replay the timeline of expected-ship
 *     drift without rebuilding it from BoM-item history.
 *
 * Versioned so future shape changes (added fields, renamed status) can
 * be migrated without a schema break.
 */
export interface BomStatusChangeEvent {
  /** Firestore doc id — auto-generated. Provided for callers that need it. */
  id?: string;
  schemaVersion: 1;
  siteId: string;
  bomItemId: string;
  bomItemName: string;
  bomItemCategory: BomItem['category'];
  fromStatus: BomItemStatus;
  toStatus: BomItemStatus;
  kind: 'forward' | 'revert';
  fromOrdinal: number;
  toOrdinal: number;
  /** UID of the user who performed the action. */
  actorUid: string;
  /** Email of the user who performed the action — denormalised for display. */
  actorEmail: string;
  /** Optional free-text reason for the transition. */
  reason?: string;
  /** ISO timestamp of the transition. Always set at write time. */
  changedAt: string;
  /** ISO date carried for SCM16 alert backfill. */
  expectedShipDate?: string;
  /** ISO date carried for SCM16 alert backfill. */
  requiredOnSiteDate?: string;
}

/**
 * Build (but do not persist) a `BomStatusChangeEvent` from a validated
 * transition. Caller hands the result to the Firestore writer in
 * `bomStatusChangeLog.ts`. Separated so the audit-row construction is
 * unit-testable without Firestore.
 */
export function buildStatusChangeEvent(args: {
  siteId: string;
  item: BomItem;
  toStatus: BomItemStatus;
  actorUid: string;
  actorEmail: string;
  reason?: string;
  /** Optional override for the timestamp — tests pass a fixed value. */
  now?: string;
  /** Opt-in to the revert branch. Defaults to false. */
  revert?: boolean;
}): { event: BomStatusChangeEvent; validation: StatusTransitionValidation } {
  const v = validateStatusTransition(args.item.status, args.toStatus, { revert: args.revert });
  if (v.kind === 'invalid') {
    throw new Error(v.reason);
  }
  if (v.kind === 'noop') {
    // Noops still return a synthetic event so callers can log the
    // attempted change for observability; persistence is gated by `kind`.
    const ord = v.ordinal;
    return {
      event: {
        schemaVersion: 1,
        siteId: args.siteId,
        bomItemId: args.item.id,
        bomItemName: args.item.name,
        bomItemCategory: args.item.category,
        fromStatus: args.item.status,
        toStatus: args.toStatus,
        kind: 'forward',
        fromOrdinal: ord,
        toOrdinal: ord,
        actorUid: args.actorUid,
        actorEmail: args.actorEmail,
        reason: args.reason,
        changedAt: args.now ?? new Date().toISOString(),
        expectedShipDate: args.item.expectedOnSiteDate,
        requiredOnSiteDate: args.item.requiredOnSiteDate,
      },
      validation: v,
    };
  }
  return {
    event: {
      schemaVersion: 1,
      siteId: args.siteId,
      bomItemId: args.item.id,
      bomItemName: args.item.name,
      bomItemCategory: args.item.category,
      fromStatus: args.item.status,
      toStatus: args.toStatus,
      kind: v.kind,
      fromOrdinal: v.fromOrdinal,
      toOrdinal: v.toOrdinal,
      actorUid: args.actorUid,
      actorEmail: args.actorEmail,
      reason: args.reason,
      changedAt: args.now ?? new Date().toISOString(),
      expectedShipDate: args.item.expectedOnSiteDate,
      requiredOnSiteDate: args.item.requiredOnSiteDate,
    },
    validation: v,
  };
}

/**
 * Aggregate counts of BoM items at each status — used by the
 * Suppliers/Overview tabs to render "12 ordered, 4 in transit, 1 on
 * site" summary chips. Returns a record with every status key present
 * (zero-valued when no items hold that status).
 */
export function summariseStatusCounts(items: readonly BomItem[]): Record<BomItemStatus, number> {
  const out = BOM_STATUS_ORDER.reduce<Record<BomItemStatus, number>>((acc, s) => {
    acc[s] = 0;
    return acc;
  }, {} as Record<BomItemStatus, number>);
  for (const item of items) {
    if (item.status in out) out[item.status] += 1;
  }
  return out;
}
